import {
  CharityVettingUrlType,
  ORGANISATION_OTHER_OPTIONS_REQUIRING_AGREMENT,
  type OrganisationOtherOptionEnum,
  OrganisationPurposeEnum,
  OrganisationSpecificTypeEnum,
} from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import agrementDateMsg from './components/steps/agrement-date-step/messages';
import arretePublicationDateMsg from './components/steps/arrete-publication-date-step/messages';
import sirenMsg from './components/steps/charity-siren-rna-step/messages';
import decreePublicationDatesMsg from './components/steps/decree-publication-dates-step/messages';
import { ORGANISATION_SITUATION_MAX_LENGTH } from './components/steps/organisation-situation-step/constants';
import organisationSituationMsg from './components/steps/organisation-situation-step/messages';
import { frDocumentPageSchemas } from './frDocumentPageSchemas';

import type {
  PageSchemaFactory,
  PageSchemaMap,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';

/**
 * Per-page Zod schemas for the FR vetting flow. Several pages are conditional on
 * earlier answers (organisationPurpose / organisationSpecificType); since each
 * page is its own form, those siblings are already-set form state read inside
 * `superRefine`. The siren/rna page validates a mutual-requirement pair.
 */

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const toStr = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const authorisedFoodAidPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ isAuthorisedFoodAid: z.boolean() }));

const organisationPurposePageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ organisationPurpose: z.nativeEnum(OrganisationPurposeEnum) }));

// organisation-specific-type — required when purpose is GENERAL_INTEREST
const organisationSpecificTypePageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        organisationPurpose: z.string().optional(),
        organisationSpecificType: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          data.organisationPurpose === OrganisationPurposeEnum.GENERAL_INTEREST &&
          !data.organisationSpecificType
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'organisationSpecificType-required',
            path: ['organisationSpecificType'],
          });
        }
      }),
  );

// decree-publication-dates — both dates required when specificType is ASSOCIATION_FONDATION_UTILITE_PUBLIQUE
const decreePublicationDatesPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        organisationSpecificType: z.string().optional(),
        decreePublicationDate: z.string().optional(),
        officialJournalPublicationDate: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          data.organisationSpecificType ===
          OrganisationSpecificTypeEnum.ASSOCIATION_FONDATION_UTILITE_PUBLIQUE
        ) {
          if (!data.decreePublicationDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(
                decreePublicationDatesMsg.charityVettingDecreePublicationDateRequiredFR,
              ),
              path: ['decreePublicationDate'],
            });
          }
          if (!data.officialJournalPublicationDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(
                decreePublicationDatesMsg.charityVettingOfficialJournalPublicationDateRequiredFR,
              ),
              path: ['officialJournalPublicationDate'],
            });
          }
        }
      }),
  );

// arrete-publication-date — required when specificType is ASSOCIATION_MOSELLE_ALSACE
const arretePublicationDatePageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        organisationSpecificType: z.string().optional(),
        arretePublicationDate: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          data.organisationSpecificType ===
            OrganisationSpecificTypeEnum.ASSOCIATION_MOSELLE_ALSACE &&
          !data.arretePublicationDate
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(
              arretePublicationDateMsg.charityVettingArretePublicationDateRequiredFR,
            ),
            path: ['arretePublicationDate'],
          });
        }
      }),
  );

// organisation-situation — required + max length when specificType is OTHER
const organisationSituationPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        organisationSpecificType: z.string().optional(),
        organisationSituation: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.organisationSpecificType !== OrganisationSpecificTypeEnum.OTHER) {
          return;
        }
        if (!data.organisationSituation) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(
              organisationSituationMsg.charityVettingOrganisationSituationRequiredFR,
            ),
            path: ['organisationSituation'],
          });
          return;
        }
        if (data.organisationSituation.length > ORGANISATION_SITUATION_MAX_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(
              organisationSituationMsg.charityVettingOrganisationSituationMaxLengthFR,
              { max: ORGANISATION_SITUATION_MAX_LENGTH },
            ),
            path: ['organisationSituation'],
          });
        }
      }),
  );

// organisation-other-option — required when purpose is OTHER
const organisationOtherOptionPageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        organisationPurpose: z.string().optional(),
        organisationOtherOption: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          data.organisationPurpose === OrganisationPurposeEnum.OTHER &&
          !data.organisationOtherOption
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'organisationOtherOption-required',
            path: ['organisationOtherOption'],
          });
        }
      }),
  );

// agrement-date — required when the chosen other-option requires an agrément
const agrementDatePageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        organisationOtherOption: z.string().optional(),
        agrementDate: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        const requiresAgrement =
          !!data.organisationOtherOption &&
          ORGANISATION_OTHER_OPTIONS_REQUIRING_AGREMENT.includes(
            data.organisationOtherOption as OrganisationOtherOptionEnum,
          );
        if (requiresAgrement && !data.agrementDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(agrementDateMsg.charityVettingAgrementDateRequiredFR),
            path: ['agrementDate'],
          });
        }
      }),
  );

// siren/rna — at least one is required; SIREN must be 9 digits when present
const sirenRnaPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({ siren: z.unknown().optional(), rna: z.unknown().optional() })
      .superRefine((data, ctx) => {
        const siren = toStr(data.siren);
        const rna = toStr(data.rna);
        if (!siren && !rna) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(sirenMsg.charityVettingSirenRnaRequiredFR),
            path: ['siren'],
          });
          return;
        }
        if (siren && siren.length !== 9) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(sirenMsg.charityVettingSirenWrongFormatFR),
            path: ['siren'],
          });
        }
      }),
  );

const foodSafetyQ3PageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ staffTrainedInFoodHygiene: z.boolean() }));

export const frPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.AUTHORISED_FOOD_AID]: authorisedFoodAidPageSchema,
  [CharityVettingUrlType.ORGANISATION_PURPOSE]: organisationPurposePageSchema,
  [CharityVettingUrlType.ORGANISATION_SPECIFIC_TYPE]: organisationSpecificTypePageSchema,
  [CharityVettingUrlType.DECREE_PUBLICATION_DATES]: decreePublicationDatesPageSchema,
  [CharityVettingUrlType.ARRETE_PUBLICATION_DATE]: arretePublicationDatePageSchema,
  [CharityVettingUrlType.ORGANISATION_SITUATION]: organisationSituationPageSchema,
  [CharityVettingUrlType.ORGANISATION_OTHER_OPTION]: organisationOtherOptionPageSchema,
  [CharityVettingUrlType.AGREMENT_DATE]: agrementDatePageSchema,
  [CharityVettingUrlType.SIREN]: sirenRnaPageSchema,
  [CharityVettingUrlType.FOOD_SAFETY_QUESTION_THREE]: foodSafetyQ3PageSchema,
  ...frDocumentPageSchemas,
};
